import { useCallback, useEffect, useRef, useState } from 'react';
import normalizeApiError from '../utils/normalizeApiError';

// loader must be memoized. No previous-scope data is exposed while a new scope loads.
export default function useScopedLoad(scope, loader, enabled = true) {
  const [state, setState] = useState({ scope: null, loading: true, data: null, error: null });
  const generation = useRef(0);
  const controller = useRef(null);
  const reload = useCallback(async () => {
    const key = ++generation.current;
    controller.current?.abort();
    controller.current = new AbortController();
    if (!enabled) { setState({ scope, loading: false, data: null, error: null }); return; }
    setState({ scope, loading: true, data: null, error: null });
    try {
      const data = await loader(controller.current.signal);
      if (key === generation.current) setState({ scope, loading: false, data, error: null });
      return data;
    } catch (error) {
      if (key !== generation.current || error?.code === 'ERR_CANCELED') return;
      setState({ scope, loading: false, data: null, error: normalizeApiError(error) });
    }
  }, [scope, loader, enabled]);
  useEffect(() => {
    reload();
    const activeController = controller.current;
    const counter = generation;
    return () => { ++counter.current; activeController?.abort(); };
  }, [reload]);
  return { ...(state.scope === scope ? state : { loading: true, data: null, error: null }), reload };
}
